import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CubeGrid } from '../ui/CountingCubes';
import { QUESTIONS, ENCOURAGEMENTS } from '../../data/curriculum';
import { Check, X, Gift } from 'lucide-react';

const HiddenBox = ({ exercise, onAnswer }) => {
  const { start, change, operation, options } = exercise;
  const correctAnswer = operation === 'add' ? start + change : start - change;
  
  const [phase, setPhase] = useState('show'); // 'show', 'cover', 'change', 'question', 'answered'
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    // Reset and start sequence
    setPhase('show');
    setSelectedAnswer(null);
    setIsCorrect(false);

    // Phase sequence
    const timer1 = setTimeout(() => setPhase('cover'), 1500);
    const timer2 = setTimeout(() => setPhase('change'), 2500);
    const timer3 = setTimeout(() => setPhase('question'), 4000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [exercise]);

  const handleAnswer = (answer) => {
    if (phase !== 'question') return;
    
    setSelectedAnswer(answer);
    const correct = answer === correctAnswer;
    setIsCorrect(correct);
    setPhase('answered');

    setTimeout(() => {
      onAnswer(correct);
    }, 1200);
  };

  const getButtonClass = (option) => {
    if (phase !== 'answered') {
      return 'btn-number-default hover:border-primary hover:scale-105';
    }
    if (option === correctAnswer) {
      return 'btn-number-correct';
    }
    if (option === selectedAnswer && !isCorrect) {
      return 'btn-number-incorrect animate-shake';
    }
    return 'btn-number-default opacity-50';
  };

  const getPhaseText = () => {
    switch (phase) {
      case 'show':
        return `Здесь ${start} кубик${start > 1 ? 'а' : ''}`;
      case 'cover':
        return 'Закрываем коробку...';
      case 'change':
        return operation === 'add' 
          ? `${change} кубик${change > 1 ? 'а' : ''} пришёл${change > 1 ? 'и' : ''}!`
          : `${change} кубик${change > 1 ? 'а' : ''} ушёл${change > 1 ? 'и' : ''}!`;
      case 'question':
      case 'answered':
        return QUESTIONS['hidden-box'];
      default:
        return '';
    }
  };

  const feedbackMessage = isCorrect 
    ? ENCOURAGEMENTS.correct[Math.floor(Math.random() * ENCOURAGEMENTS.correct.length)]
    : ENCOURAGEMENTS.incorrect[Math.floor(Math.random() * ENCOURAGEMENTS.incorrect.length)];

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
      {/* Instruction/Question */}
      <motion.h2 
        key={phase}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl font-display font-bold text-foreground mb-8 text-center"
      >
        {getPhaseText()}
      </motion.h2>

      {/* Box with cubes */}
      <div className="mb-8 relative">
        <motion.div
          className="p-6 bg-card rounded-2xl border-2 border-border shadow-card min-w-[180px] min-h-[120px] flex items-center justify-center relative overflow-hidden"
        >
          {/* Cubes (shown initially) */}
          <AnimatePresence>
            {phase === 'show' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
              >
                <CubeGrid count={start} size="lg" animate={true} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Cover */}
          <AnimatePresence>
            {(phase === 'cover' || phase === 'change' || phase === 'question' || phase === 'answered') && (
              <motion.div
                initial={{ y: -150 }}
                animate={{ y: 0 }}
                exit={{ y: -150 }}
                transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                className="absolute inset-0 bg-muted rounded-2xl flex flex-col items-center justify-center"
              >
                <Gift className="w-12 h-12 text-muted-foreground mb-2" />
                <span className="text-muted-foreground font-display font-bold">?</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Change indicator */}
        <AnimatePresence>
          {phase === 'change' && (
            <motion.div
              initial={{ opacity: 0, x: operation === 'add' ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className={`absolute top-1/2 -translate-y-1/2 ${
                operation === 'add' ? 'right-[-60px]' : 'left-[-60px]'
              } px-3 py-2 rounded-full font-display font-bold text-lg ${
                operation === 'add' 
                  ? 'bg-correct-light text-success' 
                  : 'bg-secondary-light text-secondary-foreground'
              }`}
            >
              {operation === 'add' ? `+${change}` : `-${change}`}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Answer Options */}
      <AnimatePresence>
        {(phase === 'question' || phase === 'answered') && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-4 justify-center flex-wrap"
          >
            {options.map((option, index) => (
              <motion.button
                key={option}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.1, type: 'spring' }}
                onClick={() => handleAnswer(option)}
                disabled={phase === 'answered'}
                className={getButtonClass(option)}
              >
                {phase === 'answered' && option === correctAnswer && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-correct rounded-full flex items-center justify-center"
                  >
                    <Check className="w-4 h-4 text-card" />
                  </motion.span>
                )}
                {phase === 'answered' && option === selectedAnswer && !isCorrect && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-incorrect rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4 text-card" />
                  </motion.span>
                )}
                {option}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feedback */}
      <AnimatePresence>
        {phase === 'answered' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-8 px-6 py-3 rounded-full font-display font-bold text-lg ${
              isCorrect 
                ? 'bg-correct-light text-success' 
                : 'bg-incorrect-light text-destructive'
            }`}
          >
            {feedbackMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HiddenBox;
